import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Trophy, Users, MapPin, Award, Edit, Calendar, User } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSquads } from '../../squads/hooks/useSquads';
import { teamAPI } from '../services';
import Breadcrumb from '../../components/common/Breadcrumb';
import teamImageService from '../services/teamImageService';
import { btn } from '../../utils/buttonStyles';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';

function PlayerRow({ player, theme, onClick }) {
  // Profile image is stored as /images/profile_images/{userId}/{filename} (new format)
  // or a bare filename (legacy). Use /images/ path directly; legacy falls back to no avatar.
  const profileImage = player?.user?.profile_image || player?.user?.profileImage
                    || player?.profile_image || player?.profileImage;
  const imageUrl = profileImage?.startsWith('/images/') ? resolveImageUrl(profileImage) : null;

  return (
    <div
      key={player.id}
      className={`${theme.backgroundSecondary} rounded-lg p-4 ${theme.cardHover} cursor-pointer transition-colors`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white overflow-hidden">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={player.name || player.user?.firstName || player.firstName}
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              <User className="w-6 h-6" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-x-2">
              <h3 className={`text-lg font-semibold ${theme.text}`}>
                {player.number && `#${player.number} `}
                {player.name ||
                 (player.user ? `${player.user.firstName || player.user.first_name || ''} ${player.user.lastName || player.user.last_name || ''}`.trim() : '') ||
                 `${player.firstName || player.first_name || ''} ${player.lastName || player.last_name || ''}`.trim() ||
                 'Unknown Player'}
              </h3>
              {(player.isCaptain || player.is_captain) && (
                <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full flex items-center">
                  <Award className="w-3 h-3 mr-1" />
                  Captain
                </span>
              )}
            </div>
            {player.position && (
              <p className={`${theme.textSecondary} text-sm`}>
                {player.position}
              </p>
            )}
          </div>
        </div>

        {(player.points || player.assists || player.rebounds) && (
          <div className="grid grid-cols-3 gap-6 text-center">
            {player.points && (
              <div>
                <div className={`text-lg font-bold ${theme.text}`}>{player.points}</div>
                <div className={`text-xs ${theme.textMuted}`}>PPG</div>
              </div>
            )}
            {player.assists && (
              <div>
                <div className={`text-lg font-bold ${theme.text}`}>{player.assists}</div>
                <div className={`text-xs ${theme.textMuted}`}>APG</div>
              </div>
            )}
            {player.rebounds && (
              <div>
                <div className={`text-lg font-bold ${theme.text}`}>{player.rebounds}</div>
                <div className={`text-xs ${theme.textMuted}`}>RPG</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TeamViewPage() {
  const { id, groupId } = useParams();  // groupId from /squads/:groupId/teams/:id routes
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { user } = useAuth();
  const { getGroup } = useSquads(false);  // Don't auto-load groups

  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groupData, setGroupData] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    fetchTeam();
  }, [id]);

  // Load team logo URL — new format (/images/...) is used directly; legacy filenames need presigned URL
  useEffect(() => {
    const loadLogoUrl = async () => {
      if (!team) return;
      const logo = team.team_logo || team.teamLogo;
      if (!logo) return;
      if (logo.startsWith('/images/')) {
        // New format: use directly
        setLogoUrl(logo);
      } else if (logo.includes('.')) {
        // Legacy format: fetch presigned URL
        try {
          const url = await teamImageService.getTeamLogoUrl(team.id, logo);
          if (url) setLogoUrl(url);
        } catch (err) {
          console.error('Failed to load team logo:', err);
        }
      }
    };
    loadLogoUrl();
  }, [team]);

  // Fetch group details when viewing from squad context
  useEffect(() => {
    const fetchGroupDetails = async () => {
      if (!groupId) return;
      try {
        const result = await getGroup(groupId);
        if (result.success && result.data) {
          setGroupData(result.data);
        }
      } catch (error) {
        console.error('Failed to fetch group details:', error);
      }
    };
    fetchGroupDetails();
  }, [groupId, getGroup]);

  const fetchTeam = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await teamAPI.getById(id);
      const teamData = result?.data || result;
      setTeam(teamData);
    } catch (error) {
      console.error('Error fetching team:', error);
      setError('Failed to load team details');
      toast.error('Failed to load team');
    } finally {
      setLoading(false);
    }
  };

  const isOwner = () => {
    if (!user || !team) return false;
    return (
      team.createdBy === user.id ||
      team.ownerId === user.id ||
      team.captainUserId === user.id ||
      team.captain_user_id === user.id
    );
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handlePlayerClick = (player) => {
    // Get user ID - could be in player.userId, player.user.id, or player.id
    const userHandle = player?.userHandle || player?.user?.userHandle;
    const userId = player?.userId || player?.user?.id || player?.id;
    if (userHandle || userId) {
      navigate(`/player/${userHandle || userId}`);
    }
  };

  // Build breadcrumb items based on current path (must be before early returns)
  const breadcrumbItems = useMemo(() => {
    const path = location.pathname;
    const name = team?.team_name || team?.teamName || 'Team';

    if (path.startsWith('/profile/teams')) {
      return [
        { label: 'Profile', path: '/profile' },
        { label: 'Teams', path: '/profile/teams' },
        { label: name }
      ];
    } else if (groupId || path.startsWith('/squads/')) {
      const gid = groupId || path.split('/')[2];
      const squadName = groupData?.name || groupData?.groupName || 'Squad';
      return [
        { label: 'Squads', path: '/squads' },
        { label: squadName, path: `/squads/${gid}` },
        { label: 'Teams', path: `/squads/${gid}?tab=teams` },
        { label: name }
      ];
    }

    // Default breadcrumbs
    return [
      { label: 'Teams', path: '/teams' },
      { label: name }
    ];
  }, [location.pathname, team, groupId, groupData]);

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.background} flex items-center justify-center`}>
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className={`min-h-screen ${theme.background} py-6 px-4`}>
        <div className="max-w-6xl mx-auto text-center py-12">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className={`text-xl font-semibold ${theme.text} mb-2`}>Team not found</h2>
          <p className={`${theme.textSecondary} mb-6`}>{error || 'The team you are looking for does not exist.'}</p>
          <button
            onClick={() => navigate(-1)}
            className={`${btn.base} ${btn.lg} ${theme.buttonPrimary}`}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const teamName = team.team_name || team.teamName;
  const teamLogo = team.team_logo || team.teamLogo;
  const isImageLogo = teamLogo && (teamLogo.startsWith('/images/') || teamLogo.includes('.'));
  const maxMembers = team.max_members || team.maxMembers || 0;
  const players = team.players || team.members || [];

  // Render the team logo
  const renderLogo = () => {
    if (isImageLogo && logoUrl) {
      return <img src={resolveImageUrl(logoUrl)} alt={teamName} className="w-full h-full object-cover rounded-xl" />;
    } else if (teamLogo && !isImageLogo) {
      return <span className="text-2xl">{teamLogo}</span>;
    } else {
      return teamName?.charAt(0)?.toUpperCase() || 'T';
    }
  };

  return (
    <div className={`min-h-screen ${theme.background} py-6 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-6xl mx-auto">
        {/* Breadcrumb */}
        <div className="mb-4">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        {/* Sport Type Badge - Right Aligned */}
        {team.sportType?.sportName && (
          <div className="flex justify-end mb-4">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${theme.backgroundSecondary} ${theme.text}`}>
              <Trophy className="w-4 h-4 mr-1" />
              {team.sportType.sportName}
            </span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <div className="flex items-center gap-x-4">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
                {renderLogo()}
              </div>
              <div>
                <h1 className={`text-3xl font-bold ${theme.text}`}>{teamName}</h1>
                {team.ownerInfo && (
                  <p className={`text-sm ${theme.textMuted} mt-1 flex items-center gap-1`}>
                    {team.ownerInfo.type === 'group' ? (
                      <>
                        <Users className="w-3.5 h-3.5 flex-shrink-0" />
                        <Link
                          to={`/squads/${team.ownerInfo.id}`}
                          className="text-blue-500 hover:text-blue-600 hover:underline font-medium"
                        >
                          {team.ownerInfo.name}
                        </Link>
                      </>
                    ) : (
                      <>
                        <User className="w-3.5 h-3.5 flex-shrink-0" />
                        <Link
                          to={`/player/${team.ownerInfo.id}`}
                          className="text-blue-500 hover:text-blue-600 hover:underline font-medium"
                        >
                          {team.ownerInfo.name}
                        </Link>
                      </>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>

          {isOwner() && (
            <button
              onClick={() => navigate(`${location.pathname}/edit`)}
              className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-x-2`}
            >
              <Edit className="w-4 h-4" />
              <span>Edit Team</span>
            </button>
          )}
        </div>

        {/* Team Info */}
        <div className={`${theme.card} rounded-xl ${theme.shadow} p-6 mb-8`}>
          <h2 className={`text-2xl font-bold ${theme.text} mb-4`}>About</h2>
          <p className={`${theme.textSecondary} mb-6`}>
            {team.description || 'No description available for this team.'}
          </p>

          <div className="grid md:grid-cols-3 gap-4">
            <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
              <div className={`text-2xl font-bold ${theme.text}`}>{maxMembers}</div>
              <div className={`text-sm ${theme.textMuted}`}>Max Members</div>
            </div>
            <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
              <div className={`text-2xl font-bold ${theme.text}`}>{players.length}</div>
              <div className={`text-sm ${theme.textMuted}`}>Current Players</div>
            </div>
            <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
              <div className={`text-2xl font-bold ${theme.text}`}>
                {team.createdAt ? new Date(team.createdAt).getFullYear() : '-'}
              </div>
              <div className={`text-sm ${theme.textMuted}`}>Founded</div>
            </div>
          </div>
        </div>

        {/* Team Stats */}
        {(team.wins !== undefined || team.losses !== undefined) && (
          <div className={`${theme.card} rounded-xl ${theme.shadow} p-6 mb-8`}>
            <h2 className={`text-2xl font-bold ${theme.text} mb-6 flex items-center`}>
              <Trophy className="w-6 h-6 mr-2 text-yellow-500" />
              Team Statistics
            </h2>

            <div className="grid md:grid-cols-4 gap-4">
              {team.ranking && (
                <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
                  <div className={`text-2xl font-bold ${theme.text}`}>{team.ranking}</div>
                  <div className={`text-sm ${theme.textMuted}`}>Ranking</div>
                </div>
              )}
              {team.wins !== undefined && (
                <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
                  <div className={`text-2xl font-bold text-green-600`}>{team.wins}</div>
                  <div className={`text-sm ${theme.textMuted}`}>Wins</div>
                </div>
              )}
              {team.losses !== undefined && (
                <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
                  <div className={`text-2xl font-bold text-red-600`}>{team.losses}</div>
                  <div className={`text-sm ${theme.textMuted}`}>Losses</div>
                </div>
              )}
              {team.wins !== undefined && team.losses !== undefined && (
                <div className={`${theme.backgroundSecondary} rounded-lg p-4 text-center`}>
                  <div className={`text-2xl font-bold ${theme.text}`}>
                    {team.wins + team.losses > 0
                      ? ((team.wins / (team.wins + team.losses)) * 100).toFixed(1)
                      : 0}%
                  </div>
                  <div className={`text-sm ${theme.textMuted}`}>Win Rate</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Players List */}
        <div className={`${theme.card} rounded-xl ${theme.shadow} p-6`}>
          <h2 className={`text-2xl font-bold ${theme.text} mb-6 flex items-center`}>
            <Users className="w-6 h-6 mr-2 text-blue-500" />
            Player Roster
          </h2>

          {players.length > 0 ? (
            <div className="space-y-4">
              {players.map((player, index) => (
                <PlayerRow
                  key={player.id || index}
                  player={player}
                  theme={theme}
                  onClick={() => handlePlayerClick(player)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className={`w-16 h-16 ${theme.textMuted} mx-auto mb-4`} />
              <h3 className={`text-lg font-medium ${theme.text} mb-2`}>No players found</h3>
              <p className={`${theme.textSecondary}`}>This team doesn't have any players yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
